import log from 'loglevel';
import { StoreApi } from 'zustand';

import {
  MessageDialogCloseHandler,
  MessageDialogDiscardHandler,
  MessageDialogOpenParams,
  MessageDialogState,
  MessageDialogStore,
} from './types';
import { HandlerButton } from '../../hooks/UserCommunications/useViewMessageComposerHandler';

type ZustandSet = StoreApi<MessageDialogStore>['setState'];
type ZustandGet = StoreApi<MessageDialogStore>['getState'];

export const INITIAL_STATE: MessageDialogState = {
  isOpen: false,
  dialogKey: 0,
  messageId: undefined,
  draftId: undefined,
  selectedHandlerButton: HandlerButton.UNSELECTED,
  inboxEntryId: undefined,
  scheduledEmail: undefined,
  composeToRecipients: undefined,
  closeHandler: undefined,
  discardHandler: undefined,
  aiAssisted: false,
  isAboveModal: false,
  isNewMessageExpanded: true,
  prefill: undefined,
  onSent: undefined,
  restoreSnapshotSessionId: undefined,
};

let openChain: Promise<void> = Promise.resolve();

const performOpen = async (
  set: ZustandSet,
  get: ZustandGet,
  params?: MessageDialogOpenParams,
): Promise<void> => {
  const { isOpen, closeHandler } = get();

  if (isOpen) {
    // False means nothing holds the current content; the close path already toasted why.
    if (closeHandler && (await closeHandler()) === false) {
      return;
    }
  } else {
    handleClose(set, get);
  }

  set({
    isOpen: true,
    dialogKey: get().dialogKey + 1,
    closeHandler: undefined,
    discardHandler: undefined,
    messageId: params?.messageId,
    draftId: params?.draftId,
    selectedHandlerButton:
      params?.selectedHandlerButton ?? HandlerButton.UNSELECTED,
    inboxEntryId: params?.inboxEntryId,
    scheduledEmail: params?.scheduledEmail,
    composeToRecipients: params?.composeToRecipients,
    aiAssisted: params?.aiAssisted ?? false,
    prefill: params?.prefill,
    onSent: params?.onSent,
    restoreSnapshotSessionId: params?.restoreSnapshotSessionId,
  });
};

export const handleOpen = (
  set: ZustandSet,
  get: ZustandGet,
  params?: MessageDialogOpenParams,
): Promise<void> => {
  const chained = openChain.then(() =>
    performOpen(set, get, params).catch((error) => {
      log.error('useMessageDialogStore: failed to open dialog', error);
    }),
  );
  openChain = chained;
  return chained;
};

export const handleClose = (set: ZustandSet, get: ZustandGet) => {
  set({
    ...INITIAL_STATE,
    isAboveModal: get().isAboveModal,
    isNewMessageExpanded: get().isNewMessageExpanded,
  });
};

export const handleCloseIfShowingDraft = async (
  set: ZustandSet,
  get: ZustandGet,
  draftIds: string | string[],
): Promise<void> => {
  const draftIdsArray = Array.isArray(draftIds) ? draftIds : [draftIds];
  const { isOpen, draftId, discardHandler } = get();

  if (!isOpen || !draftId || !draftIdsArray.includes(draftId)) {
    return;
  }

  const discardResult = discardHandler?.();
  handleClose(set, get);

  try {
    await discardResult;
  } catch (error) {
    log.error(
      'useMessageDialogStore: discardHandler failed during closeIfShowingDraft',
      error,
    );
  }
};

export const handleUpdateMessageId = (set: ZustandSet, messageId: string) => {
  set({ messageId });
};

export const handleUpdateDraftId = (set: ZustandSet, draftId: string) => {
  set({ draftId });
};

export const handleRegisterCloseHandler = (
  set: ZustandSet,
  handler: MessageDialogCloseHandler,
) => {
  set({ closeHandler: handler });
};

export const handleUnregisterCloseHandler = (
  set: ZustandSet,
  get: ZustandGet,
  handler: MessageDialogCloseHandler,
) => {
  if (get().closeHandler === handler) {
    set({ closeHandler: undefined });
  }
};

export const handleRegisterDiscardHandler = (
  set: ZustandSet,
  handler: MessageDialogDiscardHandler,
) => {
  set({ discardHandler: handler });
};

export const handleUnregisterDiscardHandler = (
  set: ZustandSet,
  get: ZustandGet,
  handler: MessageDialogDiscardHandler,
) => {
  if (get().discardHandler === handler) {
    set({ discardHandler: undefined });
  }
};

export const handleResetState = (set: ZustandSet, get: ZustandGet) => {
  handleClose(set, get);
};

export const handleSetIsAboveModal = (set: ZustandSet, value: boolean) => {
  set({ isAboveModal: value });
};

export const handleSetNewMessageExpanded = (
  set: ZustandSet,
  value: boolean,
) => {
  set({ isNewMessageExpanded: value });
};
